/**
 * Settings Page Component
 *
 * User settings with theme switcher and Token Usage
 */

import { useState, useEffect } from "react";
import { useTheme } from "../context/ThemeContext";
import apiClient from "../services/api/client"; // Adjust path if your structure is different

type Theme = "light" | "dark" | "auto";

export function Settings() {
  const { theme, setTheme } = useTheme();

  // --- TOKEN USAGE STATES ---
  const [loadingTokens, setLoadingTokens] = useState<boolean>(true);
  const [tokenData, setTokenData] = useState<{
    totalUsed: number;
    percentUsed: number | null;
    isUnlimited: boolean;
    remaining: string | number;
    limit: number | null;
  } | null>(null);
  const [planDetails, setPlanDetails] = useState<{
    planName: string;
    isActive: boolean;
  } | null>(null);

  const themes: { value: Theme; label: string; description: string }[] = [
    {
      value: "light",
      label: "Light",
      description: "Bright and clear interface",
    },
    {
      value: "dark",
      label: "Dark",
      description: "Easy on the eyes in low light",
    },
    {
      value: "auto",
      label: "Auto",
      description: "Matches your system preference",
    },
  ];

  const handleThemeChange = (newTheme: "light" | "dark" | "auto") => {
    setTheme(newTheme);
  };

  // --- FETCH TOKEN USAGE ---
  useEffect(() => {
    const fetchTokenUsage = async () => {
      setLoadingTokens(true);
      try {
        // Fetch subscription status and token usage in parallel
        const [statusRes, tokenRes] = await Promise.all([
          apiClient.get("/api/v1/subscription/status").catch(() => null),
          apiClient.get("/api/v1/tokens/usage"),
        ]);

        // Parse subscription status
        const statusData = statusRes?.data?.data || statusRes?.data;
        if (statusData) {
          setPlanDetails({
            planName:
              statusData.plan?.name || statusData.planName || "Active Plan",
            isActive:
              statusData.status?.toLowerCase() === "active" ||
              statusData.hasActiveSubscription === true,
          });
        }

        // Parse token usage
        const rawData = tokenRes?.data?.data || tokenRes?.data || tokenRes;

        if (rawData && (rawData.success || rawData.usage)) {
          setTokenData({
            totalUsed: rawData.usage?.totalTokens || 0,
            percentUsed: Number(rawData.percentUsed) || 0,
            isUnlimited: rawData.limit?.unlimited === true,
            remaining: rawData.remaining || 0,
            limit: rawData.limit?.tokenLimit || null,
          });
        }
      } catch (error) {
        console.error("Failed to fetch token usage:", error);
      } finally {
        setLoadingTokens(false);
      }
    };

    fetchTokenUsage();
  }, []);

  return (
    <div className="h-full flex flex-col bg-[#FAF6F1] dark:bg-[#1A2D47]">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto p-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-[#1D1B19] dark:text-[#E3E6EA] text-3xl font-semibold mb-2">
              Settings
            </h1>
            <p className="text-[#8E8378] dark:text-[#9AAABF] text-base">
              Customize your DuneWorks experience
            </p>
          </div>

          {/* Divider */}
          <div className="w-full h-[1px] bg-[#E5DDD3] dark:bg-[#2C405F] mb-8" />

          <div className="space-y-8">
            {/* --- TOKEN USAGE SECTION --- */}
            <div>
              <h2 className="text-[#1D1B19] dark:text-[#E3E6EA] text-xl font-semibold mb-2">
                Token Usage
              </h2>
              <p className="text-[#8E8378] dark:text-[#9AAABF] text-sm mb-4">
                Monitor your current billing cycle usage
              </p>
              <div className="bg-[#FDFAF7] dark:bg-[#122135] border border-[#E5DDD3] dark:border-[#455D7D] rounded-xl p-5">
                {loadingTokens ? (
                  <div className="flex justify-center items-center py-2">
                    <div className="w-5 h-5 border-2 border-[#2C405F] dark:border-[#6E829E] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : tokenData ? (
                  <>
                    {/* Plan info row */}
                    {planDetails && (
                      <div className="flex items-center gap-2 mb-4">
                        <svg
                          className="w-5 h-5 text-[#2C405F] dark:text-[#6E829E] flex-shrink-0"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                          <path d="M9 12l2 2 4-4" />
                        </svg>
                        <span className="text-sm font-semibold text-[#1D1B19] dark:text-[#E3E6EA]">
                          {planDetails.planName}
                        </span>
                        <span
                          className={`ml-auto text-[10px] font-bold tracking-wide uppercase px-2 py-0.5 rounded-full ${
                            planDetails.isActive
                              ? "text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800"
                              : "text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800"
                          }`}
                        >
                          {planDetails.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    )}

                    {/* Usage stats row */}
                    <div className="flex justify-between w-full text-sm font-semibold mb-3">
                      <span className="text-[#1D1B19] dark:text-[#E3E6EA]">
                        {tokenData.isUnlimited ? (
                          "Unlimited Tokens"
                        ) : (
                          <>
                            {tokenData.totalUsed.toLocaleString()} Used
                            <span className="text-[#8E8378] dark:text-[#9AAABF] ml-1">
                              ({(tokenData.percentUsed || 0).toFixed(1)}%)
                            </span>
                          </>
                        )}
                      </span>
                      {!tokenData.isUnlimited && tokenData.limit && (
                        <span className="text-[#8E8378] dark:text-[#9AAABF]">
                          {tokenData.limit.toLocaleString()} Limit
                        </span>
                      )}
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-[#E5DDD3] dark:bg-[#2C405F] rounded-full h-3 overflow-hidden shadow-inner">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ease-out bg-[#2C405F] dark:bg-[#6E829E]`}
                        style={{
                          width: tokenData.isUnlimited
                            ? "100%"
                            : `${Math.min(tokenData.percentUsed || 0, 100)}%`,
                        }}
                      ></div>
                    </div>

                    {/* Remaining tokens */}
                    {!tokenData.isUnlimited && (
                      <div className="w-full text-right mt-2">
                        <span className="text-xs text-[#8E8378] dark:text-[#9AAABF]">
                          {Number(tokenData.remaining).toLocaleString()}{" "}
                          remaining
                        </span>
                      </div>
                    )}

                    {/* Footer note */}
                    <p className="text-[10px] text-[#8E8378] dark:text-[#6E829E] mt-3 text-center">
                      Tokens automatically reset at the end of your billing
                      cycle.
                    </p>
                  </>
                ) : (
                  <p className="text-[#8E8378] dark:text-[#9AAABF] text-sm text-center">
                    Token data unavailable.
                  </p>
                )}
              </div>
            </div>
            {/* --- END OF TOKEN USAGE SECTION --- */}

            {/* Appearance Section */}
            <div>
              <h2 className="text-[#1D1B19] dark:text-[#E3E6EA] text-xl font-semibold mb-2">
                Appearance
              </h2>
              <p className="text-[#8E8378] dark:text-[#9AAABF] text-sm mb-6">
                Choose how DuneWorks looks to you
              </p>

              <div className="space-y-3">
                {themes.map((themeOption) => (
                  <button
                    key={themeOption.value}
                    onClick={() => handleThemeChange(themeOption.value)}
                    className={`w-full flex items-start gap-4 p-4 rounded-xl border transition-all ${
                      theme === themeOption.value
                        ? "border-[#2C405F] dark:border-[#6E829E] bg-[#FCFBFA] dark:bg-[#2C405F]"
                        : "border-[#E5DDD3] dark:border-[#455D7D] bg-[#FDFAF7] dark:bg-[#122135] hover:bg-[#FCFBFA] dark:hover:bg-[#2C405F]"
                    }`}
                  >
                    {/* Radio Button */}
                    <div className="flex-shrink-0 mt-0.5">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                          theme === themeOption.value
                            ? "border-[#2C405F] dark:border-[#6E829E]"
                            : "border-[#8E8378] dark:border-[#9AAABF]"
                        }`}
                      >
                        {theme === themeOption.value && (
                          <div className="w-2.5 h-2.5 bg-[#2C405F] dark:bg-[#6E829E] rounded-full" />
                        )}
                      </div>
                    </div>

                    {/* Theme Info */}
                    <div className="flex-1 text-left">
                      <div className="text-[#1D1B19] dark:text-[#E3E6EA] text-base font-semibold mb-1">
                        {themeOption.label}
                      </div>
                      <div className="text-[#8E8378] dark:text-[#9AAABF] text-sm">
                        {themeOption.description}
                      </div>
                    </div>

                    {/* Theme Preview Icon */}
                    <div className="flex-shrink-0">
                      {themeOption.value === "light" && (
                        <svg
                          className="w-8 h-8 text-[#8E8378] dark:text-[#9AAABF]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                          />
                        </svg>
                      )}
                      {themeOption.value === "dark" && (
                        <svg
                          className="w-8 h-8 text-[#8E8378] dark:text-[#9AAABF]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                          />
                        </svg>
                      )}
                      {themeOption.value === "auto" && (
                        <svg
                          className="w-8 h-8 text-[#8E8378] dark:text-[#9AAABF]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Account Section */}
            <div>
              <h2 className="text-[#1D1B19] dark:text-[#E3E6EA] text-xl font-semibold mb-2">
                Account
              </h2>
              <p className="text-[#8E8378] dark:text-[#9AAABF] text-sm mb-4">
                Manage your account settings
              </p>
              <div className="bg-[#FDFAF7] dark:bg-[#122135] border border-[#E5DDD3] dark:border-[#455D7D] rounded-xl p-4">
                <p className="text-[#8E8378] dark:text-[#9AAABF] text-sm text-center">
                  Coming soon
                </p>
              </div>
            </div>

            {/* Notifications Section */}
            <div>
              <h2 className="text-[#1D1B19] dark:text-[#E3E6EA] text-xl font-semibold mb-2">
                Notifications
              </h2>
              <p className="text-[#8E8378] dark:text-[#9AAABF] text-sm mb-4">
                Control how you receive notifications
              </p>
              <div className="bg-[#FDFAF7] dark:bg-[#122135] border border-[#E5DDD3] dark:border-[#455D7D] rounded-xl p-4">
                <p className="text-[#8E8378] dark:text-[#9AAABF] text-sm text-center">
                  Coming soon
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
